/**
 * reportClientError — the browser half of the one client→server error channel
 * (#1654). Server half: backend/src/routes/clientErrors.js. Staff read it on
 * /admin/stats ("Browser errors"). Contract: docs/architecture/client-error-channel.md.
 *
 * Every `console.error` on a family-facing failure path should go through
 * this instead: the console line reaches the family's own laptop, which nobody
 * at TRF owns, and this is the only thing that reaches staff.
 *
 * What goes on the wire, and what deliberately does not:
 *   - `token`        the event token (`area.surface.what_failed`) — the grep
 *                    handle, from a fixed lower-case alphabet
 *   - `route`        `window.location.pathname` ONLY — never the query string
 *                    (codes and tokens ride there) and never the fragment
 *   - `occurred_at`  the browser's clock
 *   The `error` argument is logged locally and NEVER sent: a message or a stack
 *   can carry a family's name or e-mail, and the server's schema rejects any
 *   such field with a 400 anyway.
 *
 * Behaviour:
 *   - fire-and-forget: `keepalive` so a report survives the tab closing, no
 *     await, no retry — the failure being reported is often the network, and
 *     this must never become the second outage
 *   - never throws, whatever `fetch` does (rejects, throws, is undefined)
 *   - at most ONE report per token per page session (module-level Set); the
 *     local console line still fires every time
 */

const REPORT_URL = '/api/client-errors';

/** Tokens already reported since this page loaded. */
const reportedTokens = new Set<string>();

export function reportClientError(token: string, error?: unknown): void {
  console.error(token, error);

  if (reportedTokens.has(token)) return;
  reportedTokens.add(token);

  try {
    if (typeof fetch !== 'function') return;
    const route = typeof window !== 'undefined' && window.location ? window.location.pathname : '/';
    const body = JSON.stringify({ token, route, occurred_at: new Date().toISOString() });
    const pending = fetch(REPORT_URL, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
    });
    // A rejected report is swallowed on purpose: there is nobody to tell, and
    // retrying would be the per-surface outage-amplifier this channel exists
    // to avoid.
    void Promise.resolve(pending).catch(() => undefined);
  } catch {
    // fetch threw synchronously (jsdom, a locked-down embed): same answer.
  }
}

/** Test hook: clear the per-page dedupe set. Not for product code. */
export function _resetReportedTokensForTests(): void {
  reportedTokens.clear();
}
